/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../../fragments'
import moment from 'moment'

import {
  Input,
  Button,
  SelectWithData,
  DivisionSelect,
  PayrollCycleSelect,
  KeyValueTable as InputTable
} from '../'

import {
  getDateOfBirthFromSouthAfricanID
} from '../../utils'

class IdentityClientInput extends React.Component {
  static propTypes = {
    employer: PropTypes.object.isRequired,
    className: PropTypes.string,
    onCancel: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired
  }

  constructor(props) {
    super(props)
    this.state = {
      identityExistsAlready: false,
      // identity info
      firstName: '',
      lastName: '',
      language: '',
      identityType: '',
      identityValue: '',
      // client info
      division: null,
      payrollcycle: null,
      // validation
      displayInputError: [ false ]
    }
  }

  canSave = () => {
    if ((this.state.identityType === "za_id" &&
            this.state.identityValue.length === 13 &&
            !isNaN(this.state.identityValue) &&
            this.state.firstName &&
            this.state.lastName &&
            this.state.language &&
            this.state.dateOfBirth
          ) ||
        (this.state.identityType === "passport" &&
          this.state.firstName &&
          this.state.lastName &&
          this.state.language
        )) {
      return true
    } else {
      let errorFields = []
      errorFields.push(true)

      if (!this.state.identityType) {
        errorFields.push("identityType")
      }
      if (!this.state.identityValue) {
        errorFields.push("identityValue")
      }
      if (this.state.identityType === "za_id" &&
          (this.state.identityValue.length !== 13 ||
          isNaN(this.state.identityValue))) {
        // something's wrong with id type/value combination input
        errorFields.push("identityValue")
        errorFields.push("identityType")
      }
      if (!this.state.firstName) {
        errorFields.push("firstName")
      }
      if (!this.state.lastName) {
        errorFields.push("lastName")
      }
      if (!this.state.language) {
        errorFields.push("language")
      }

      if (!this.state.division) {
        errorFields.push("division")
      }
      if (!this.state.payrollcycle) {
        errorFields.push("payrollcycle")
      }

      this.setState({
        displayInputError: errorFields
      })

      return false
    }
  }

  save = () => {
    if (this.canSave()) {
      let input = {
        firstName: this.state.firstName,
        lastName: this.state.lastName,
        language: this.state.language,
        deceased: false,
        status: "non_member_inactive",
        identityType: this.state.identityType,
        identityValue: this.state.identityValue
      }

      if (this.state.dateOfBirth) {
        input.dateOfBirth = moment(this.state.dateOfBirth).format("YYYY-MM-DD")
      }

      this.props.addIdentity({ variables: { input }})
      .then(this.setState({ displayInputError: [ false ] }))
      .then(({ data }) => {
        console.log("Identity saved successfully")
        let identity = data.identityMutate.identity
        this.props.addClient({
          variables: {
            input: {
              identity: identity.id,
              division: this.state.division,
              payrollcycle: this.state.payrollcycle,
              status: "pending",
              statusReason: "Require validation/activation"
            }
          }
        })
        .then(this.setState({ displayInputError: [ false ] }))
        .then(({ data }) => {
          console.log("Client saved successfully")
          this.props.onSave(identity, data.clientMutate.client.id)
        })
        .catch((error) => {
          console.log('there was an error saving the client', error);
        })
      })
      .catch((error) => {
        console.log('there was an error saving the identity', error);
      })
    }
  }

  autoFillAfterIdentityNumberInput = (e) => {
    let idNumber = e.target.value

    if (this.state.identityType === "za_id") {
      let dob = getDateOfBirthFromSouthAfricanID(idNumber)

      this.setState({
        identityValue: idNumber,
        dateOfBirth: dob
      })
    }
  }

  handleIdentityTypeInputChange = (e) => {
    let idType = e.target.value

    if (idType === "za_id"
      && this.state.identityValue !== ''
      && this.state.dateOfBirth === null) {

      let dob = getDateOfBirthFromSouthAfricanID(this.state.identityValue)

      this.setState({
        identityType: idType,
        dateOfBirth: dob
      })
    } else {
      this.setState({
        identityType: idType
      })
    }

    if (idType === "za_id" && this.state.identityValue.length === 13) {
      // check for existing identity
      this.props.data.refetch({ idNumber: this.state.identityValue })
      // form fields will auto-fill via componentWillReceiveProps if identity exists
    }
  }

  render() {
    let inputFields = {
      "Identity type": <SelectWithData
        id="identityType"
        forField="IdentityIdentityType"
        value={this.state.identityType}
        onChange={this.handleIdentityTypeInputChange}
        hasError={this.state.identityValue.length > 0 && this.state.identityType.length === 0}
      />,
      "Identity number":  <Input
        id="identityValue"
        placeholder='Identity number'
        value={this.state.identityValue || ""}
        onChange={(e) => this.setState({identityValue: e.target.value})}
        onBlur={this.autoFillAfterIdentityNumberInput}
        hasError={this.state.displayInputError[0] && this.state.displayInputError.includes("identityValue")}
      />,
      "First name": <Input
        id="firstName"
        placeholder='First name'
        value={this.state.firstName || ""}
        onChange={(e) => this.setState({firstName: e.target.value})}
        hasError={this.state.displayInputError[0] && this.state.displayInputError.includes("firstName")}
      />,
      "Last name":  <Input
        id="lastName"
        placeholder='Last name'
        value={this.state.lastName || ""}
        onChange={(e) => this.setState({lastName: e.target.value})}
        hasError={this.state.displayInputError[0] && this.state.displayInputError.includes("lastName")}
      />,
      "Language": <SelectWithData
        id="language"
        forField="IdentityLanguage"
        value={this.state.language}
        onChange={(e) => this.setState({language: e.target.value})}
        hasError={this.state.displayInputError[0] && this.state.displayInputError.includes("language")}
      />,
      division: <DivisionSelect
        employer={this.props.employer.id}
        value={this.state.division}
        onChange={(e) => this.setState({ division: e.target.value })}
        hasError={this.state.displayInputError[0] && this.state.displayInputError.includes("division")}
      />,
      "Payroll cycle": <PayrollCycleSelect
        employer={this.props.employer.id}
        onChange={(e) => this.setState({ payrollcycle: e.target.value })}
        hasError={this.state.displayInputError[0] && this.state.displayInputError.includes("payrollcycle")}
      />
    }

    return (
      <div className={`${this.props.className ? this.props.className : ''}`}>
        {this.props.heading
          ? <div className="slds-m-bottom_x-small">
              {this.props.heading}
            </div>
          : <span></span>
        }
        <InputTable
          data={inputFields}
          keyColumnSize="1-of-2"
        />
        <div className="slds-m-top_medium slds-clearfix">
          <div className="slds-float_right">
            <Button
              onClick={this.props.onCancel}
              minWidth={120}
              className="u-text-caps">
              Cancel
            </Button>{' '}
            <Button
              type="brand"
              onClick={this.save}
              minWidth={120}
              className="u-text-caps">
              Save
            </Button>
          </div>
        </div>
      </div>
    )
  }
}

const addIdentity = gql`
  mutation addIdentity($input: IdentityMutationInput!) {
    identityMutate(input: $input) {
      identity {
        ...Identity
        client {
          id
        }
        defaultAddressType
        addresses {
          edges {
            node {
              ...Address
            }
          }
        }
      }
    }
  }
  ${fragments.identity}
  ${fragments.address}
`

const addClient = gql`
  mutation addClient($input: ClientMutationInput!) {
    clientMutate(input: $input) {
      client {
        ...Client
        identity {
          id
        }
      }
    }
  }
  ${fragments.client}
`

const withIdentityMutate =
  graphql(addIdentity, {
    name: 'addIdentity'
  })

const withClientMutate =
  graphql(addClient, {
    name: 'addClient'
  })

export default compose(
  withIdentityMutate,
  withClientMutate
)(IdentityClientInput)



// WEBPACK FOOTER //
// ./src/components/identity-client-input/index.js